<template>
  <section v-if="shouldRenderTabs">
    <v-tabs
      :model-value="value"
      grow
      align-center
      class="mb-6"
      color="primary"
      @update:modelValue="handleTabUpdate">
      <v-tab :value="DepartTypeEnum.AssetVisit" data-testid="yard-spot-detail-truck-tab">
        {{ t('yard.common.terms.truck') }}
      </v-tab>
      <v-tab :value="DepartTypeEnum.AssetContainer" data-testid="yard-spot-detail-assets-tab">
        {{ t('global.specialtyTerms.assets') }}
      </v-tab>
    </v-tabs>

    <v-tabs-window :model-value="value" @update:modelValue="handleTabUpdate">
      <v-tabs-window-item :key="DepartTypeEnum.AssetVisit" :value="DepartTypeEnum.AssetVisit">
        <yard-view-asset-visit-detail-data
          @assign="handleReassignSpotSelection"
          allow-container-status-update
          :spot-assignment="spotAssignment"
          :asset-visit="spotAssignment.assetVisit" />
      </v-tabs-window-item>
      <v-tabs-window-item
        :key="DepartTypeEnum.AssetContainer"
        :value="DepartTypeEnum.AssetContainer">
        <yard-view-asset-container-detail-data
          @assign="handleReassignSpotSelection"
          :asset-container="spotAssignment.assetContainer" />
      </v-tabs-window-item>
    </v-tabs-window>
  </section>

  <section v-else>
    <yard-view-asset-visit-detail-data
      v-if="spotAssignment.assetVisit"
      allow-container-status-update
      :spot-assignment="spotAssignment"
      @assign="handleReassignSpotSelection"
      :asset-visit="spotAssignment.assetVisit" />
    <yard-view-asset-container-detail-data
      v-else-if="spotAssignment.assetContainer"
      @assign="handleReassignSpotSelection"
      :asset-container="spotAssignment.assetContainer" />
  </section>
</template>
<script>
import { defineComponent, computed } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import { useEventHub, useNotify } from '@/composables';
import { first } from 'lodash';

import YardViewAssetVisitDetailData from '@/modules/yard/components/AssetVisit/Detail/YardViewAssetVisitDetailData.vue';
import YardViewAssetContainerDetailData from '@/modules/yard/components/AssetContainer/YardViewAssetContainerDetailData.vue';
import { DepartTypeEnum } from '@nova/core';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewSpotDetailAssigned',
  components: { YardViewAssetVisitDetailData, YardViewAssetContainerDetailData },
  emits: ['update'],
  props: {
    value: { type: String, required: true },
    spotAssignment: { type: Object, required: true }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const yardStore = useYard();
    const notify = useNotify();
    const handleTabUpdate = value => emit('update', value);

    const shouldRenderTabs = computed(() => {
      return props.spotAssignment?.assetVisit?.id && props.spotAssignment?.assetContainer?.id;
    });

    const handleReassignSpotSelection = async spotId => {
      const newSpot = yardStore.spotsRef.value.find(spot => spot.id === spotId);

      if (newSpot?.id && props.spotAssignment?.id) {
        const oldSpot = yardStore.spotsRef.value.find(
          spot => spot.id === props.spotAssignment.spotId
        );

        return yardStore
          .reassignSpotAssignment(props.spotAssignment.id, {
            spotId: newSpot.id,
            oldSpotId: oldSpot.id,
            moveContainer: shouldRenderTabs.value && props.value === DepartTypeEnum.AssetContainer
          })
          .then(spotAssignment => {
            yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.REASSIGN, {
              spot: { ...newSpot, spotAssignments: { ...first(oldSpot.spotAssignments) } },
              entryPoint: 'Select from List',
              assetVisit: props.spotAssignment.assetVisit ?? props.spotAssignment.assetContainer,
              metadata: {
                old_spot_id: oldSpot.id,
                old_spot_code: oldSpot.code
              }
            });

            eventHub.$emit(yardStore.SPOT_ASSIGNMENT_CHANGED, {
              assetVisit: spotAssignment.assetVisit,
              newSpotCode: spotAssignment.spot.code,
              newSpotAreaName: spotAssignment.spot.spotArea.name
            });

            yardStore.selectedSpotRef.value = newSpot;
          });
      } else {
        notify(t('yard.pages.yardView.messages.invalidSpotSelection'), 'error');
      }
    };

    const eventHub = useEventHub();

    return {
      t,
      handleReassignSpotSelection,
      handleTabUpdate,
      shouldRenderTabs,
      DepartTypeEnum
    };
  }
});
</script>
